import { useEffect, useRef } from 'react'
import { getAllMessageEventByBookAndMessage, getEventHomeLast } from '../../../services/sBook/message-log.service'

/*
 * Reemplazo de useGetMessagesByBookByMessage.
 *
 * IMPORTANTE:
 * Si useOfMessajeEvent.js contiene otros hooks, no reemplaces el archivo
 * completo: agrega useRef al import de React y reemplaza solamente esta función.
 */
const useGetMessagesByBookByMessage = (
    bookId,
    messageId,
    inputParam_FireUpdateBoxListMessageEventControl,
    setMessageEventList,
    inputParam_SetterFireUpdateBoxListMessageEventControl
) => {
    /*
     * Guarda la última combinación que ya inició una carga.
     * No dependemos de messageEventList para saber si hay que volver a consultar.
     */
    const lastLoadedKeyRef = useRef(null);

    /*
     * Evita dos requests simultáneos para el mismo book/message.
     * Esto también evita la doble llamada típica de React.StrictMode
     * mientras el primer request sigue en curso.
     */
    const requestInFlightKeyRef = useRef(null);

    /*
     * Permite descartar una respuesta antigua si el usuario cambia de
     * book/message antes de que termine el request anterior.
     */
    const currentKeyRef = useRef(null);

    /*
     * Usamos refs para leer los valores más recientes dentro del async
     * sin convertir las funciones recibidas por props en causas de re-ejecución.
     */
    const fireUpdateRef = useRef(inputParam_FireUpdateBoxListMessageEventControl);
    const setterFireUpdateRef = useRef(inputParam_SetterFireUpdateBoxListMessageEventControl);

    fireUpdateRef.current = inputParam_FireUpdateBoxListMessageEventControl;
    setterFireUpdateRef.current = inputParam_SetterFireUpdateBoxListMessageEventControl;

    useEffect(() => {
        const hasValidIds =
            bookId !== null &&
            bookId !== undefined &&
            messageId !== null &&
            messageId !== undefined;

        if (!hasValidIds) {
            currentKeyRef.current = null;
            return;
        }

        const requestKey = `${bookId}|${messageId}`;
        currentKeyRef.current = requestKey;

        /*
         * Se carga únicamente cuando:
         * 1) cambió bookId/messageId; o
         * 2) el padre solicitó explícitamente una actualización.
         */
        const shouldReload =
            inputParam_FireUpdateBoxListMessageEventControl === true ||
            lastLoadedKeyRef.current !== requestKey;

        if (!shouldReload) {
            return;
        }

        /*
         * Si ya hay una llamada para exactamente la misma combinación,
         * no generamos otra.
         */
        if (requestInFlightKeyRef.current === requestKey) {
            return;
        }

        /*
         * Se marca antes del await. Así un render provocado por otro estado
         * no puede volver a interpretar esta misma combinación como "no cargada".
         */
        lastLoadedKeyRef.current = requestKey;
        requestInFlightKeyRef.current = requestKey;

        const getData = async () => {
            try {
                const serviceResponse =
                    await getAllMessageEventByBookAndMessage(bookId, messageId);

                /*
                 * Si mientras esperábamos cambió el book/message, ignoramos
                 * esta respuesta porque ya pertenece a una selección anterior.
                 */
                if (currentKeyRef.current !== requestKey) {
                    return;
                }

                const response = serviceResponse.data;

                if (response.succeeded) {
                    const data = (response.data ?? []).map(element => ({
                        ...element,
                        messageEventName:
                            element.messageEventName &&
                            element.messageEventName.indexOf('#') !== -1
                                ? element.messageEventName.split('#')[0]
                                : element.messageEventName
                    }));

                    setMessageEventList(prevState => ({
                        ...prevState,
                        fireUpdate: true,
                        data
                    }));
                }
                else {
                    setMessageEventList(prevState => ({
                        ...prevState,
                        fireUpdate: true,
                        data: []
                    }));
                }
            }
            catch (error) {
                if (currentKeyRef.current !== requestKey) {
                    return;
                }

                setMessageEventList(prevState => ({
                    ...prevState,
                    fireUpdate: true,
                    data: []
                }));
            }
            finally {
                if (requestInFlightKeyRef.current === requestKey) {
                    requestInFlightKeyRef.current = null;
                }

                /*
                 * Si el trigger externo está activo al terminar esta carga,
                 * lo apagamos incluso si el request terminó con error.
                 *
                 * Al leerlo desde un ref también cubrimos el caso en que el
                 * trigger pasó a true mientras este request ya estaba en curso.
                 */
                if (
                    currentKeyRef.current === requestKey &&
                    fireUpdateRef.current === true &&
                    typeof setterFireUpdateRef.current === 'function'
                ) {
                    setterFireUpdateRef.current(false);
                }
            }
        };

        getData();

        return () => {
            /*
             * No cancelamos artificialmente el request aquí para no provocar
             * una segunda llamada por el ciclo adicional de efectos que
             * React.StrictMode realiza en desarrollo.
             *
             * La respuesta se valida contra currentKeyRef antes de modificar
             * el estado, por lo que una respuesta de otro book/message se ignora.
             */
            if (currentKeyRef.current === requestKey) {
                currentKeyRef.current = null;
            }
        };
    }, [
        bookId,
        messageId,
        inputParam_FireUpdateBoxListMessageEventControl,
        setMessageEventList
    ]);
};

const useGetEventHomeLast = (messageEventList, setMessageEventList, numberOfItems, setNumberOfItems, dataFilled, setDataFilled) => {    
    useEffect(()=> {
        const getData = async () => {
            try {
                let serviceResponse = await getEventHomeLast(numberOfItems);
                const response = serviceResponse.data;

                if (response.succeeded) {
                    setDataFilled(true);
                    setMessageEventList(prevState => ({
                        ...prevState,
                        data: response.data
                    }));
    
                    setNumberOfItems(numberOfItems + 10)
                }
                else {
                    setDataFilled(true);
                    setMessageEventList(prevState => ({
                        ...prevState,
                        data: []
                    }));
                }
            }
            catch (error) {
                setDataFilled(true);
                setMessageEventList(prevState => ({
                    ...prevState,
                    data: []
                }));
            }
        }
        

        if (dataFilled === false && messageEventList.data === null && numberOfItems > 0) {
            getData()
        }
        
    }, [messageEventList, setMessageEventList, numberOfItems, setNumberOfItems, dataFilled, setDataFilled]);
}

export {
    useGetMessagesByBookByMessage,
    useGetEventHomeLast
}